import { CURRENCY_CODE } from '../lib/constants';
import type { SanityImageProps } from '../sanity/utils/fragments/image-fragment';
import type { CartFragment } from './cart-fragment';
import type { Cart, Connection, Product, ProductVariant } from './types';

export const reshapeCart = (cart: CartFragment): Cart => {
  if (!cart.cost?.totalTaxAmount) {
    cart.cost.totalTaxAmount = {
      amount: '0.0',
      currencyCode: CURRENCY_CODE,
    } as const;
  }

  return {
    ...cart,
    lines: removeEdgesAndNodes(cart.lines),
  };
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const removeEdgesAndNodes = (array: Connection<any>) => {
  return array.edges.map((edge) => edge?.node);
};

// Export types for reuse in other components
export interface ProductCardData {
  title: string;
  slug: string;
  productGid: string;
  variantGid: string;
  price: number;
  compareAtPrice?: number | null;
  category: string;
  mainImage?: SanityImageProps | null;
  allowBackorder: boolean;
  preOrderTag?: string | null;
  squareMetersPerBox?: number | null;
}

export interface ProductPageData {
  productGid: string;
  title: string;
  category: string;
  allowBackorder: boolean;
  preOrderTag?: string | null;
  mainImage?: SanityImageProps | null;
  squareMetersPerBox?: number | null;
  variants: Array<{
    gid: string | null;
    title: string;
    price: number;
    compareAtPrice?: number | null;
    option1: string;
    option2: string | null;
    option3: string | null;
  } | null>;
}

// Adapter for Sanity product card data to cart-compatible format
export function adaptProductCardToCart(productCard: ProductCardData): {
  product: Product;
  variant: ProductVariant;
} {
  return {
    product: {
      category: productCard.category,
      gid: productCard.productGid,
      title: productCard.title,
      price: productCard.price,
      compareAtPrice: productCard.compareAtPrice,
      image: productCard.mainImage?.asset?._ref || '',
      allowBackorder: productCard.allowBackorder,
      preOrderTag: productCard.preOrderTag,
      squareMetersPerBox: productCard.squareMetersPerBox ?? null,
    },
    variant: {
      gid: productCard.variantGid,
      title: productCard.title, // Use product title as variant title for simple products
      price: productCard.price,
      compareAtPrice: productCard.compareAtPrice,
      option1: 'Default Title',
      option2: '',
      option3: '',
    },
  };
}

// Adapter for ProductPageProps (PDP data) to cart-compatible format
export function adaptProductPageToCart(
  productPage: ProductPageData
): { product: Product; variant: ProductVariant } | null {
  const firstVariant = productPage.variants?.find((v) => v !== null);

  if (!productPage.productGid || !firstVariant?.gid) {
    return null;
  }

  return {
    product: {
      category: productPage.category,
      gid: productPage.productGid,
      title: productPage.title,
      price: firstVariant.price,
      compareAtPrice: firstVariant.compareAtPrice,
      image: productPage.mainImage?.asset?._ref || '',
      allowBackorder: productPage.allowBackorder,
      preOrderTag: productPage.preOrderTag,
      squareMetersPerBox: productPage.squareMetersPerBox ?? null,
    },
    variant: {
      gid: firstVariant.gid,
      title: firstVariant.title,
      price: firstVariant.price,
      compareAtPrice: firstVariant.compareAtPrice,
      option1: firstVariant.option1,
      option2: firstVariant.option2 || '',
      option3: firstVariant.option3 || '',
    },
  };
}

// Utility function to safely convert product page props to ProductPageData
export function toProductPageData(product: {
  productGid?: string | null;
  title: string;
  category: string;
  allowBackorder?: boolean | null;
  preOrderTag?: string | null;
  mainImage?: SanityImageProps | null;
  squareMetersPerBox?: number | null;
  variants?: Array<{
    gid: string | null;
    title: string;
    price: number;
    compareAtPrice?: number | null;
    option1: string;
    option2: string | null;
    option3: string | null;
  } | null>;
}): ProductPageData | null {
  if (!product.productGid || !product.variants) {
    return null;
  }

  return {
    productGid: product.productGid,
    title: product.title,
    category: product.category,
    allowBackorder: product.allowBackorder ?? false,
    preOrderTag: product.preOrderTag,
    mainImage: product.mainImage,
    squareMetersPerBox: product.squareMetersPerBox ?? null,
    variants: product.variants,
  };
}

// Utility function to safely convert sample product props to ProductCardData
export function toProductCardData(product: {
  title: string;
  slug: string;
  productGid?: string | null;
  variantGid?: string | null;
  price: number;
  compareAtPrice?: number | null;
  category: string;
  mainImage?: SanityImageProps | null;
  allowBackorder?: boolean | null;
  preOrderTag?: string | null;
  squareMetersPerBox?: number | null;
}): ProductCardData | null {
  if (!product.productGid || !product.variantGid) {
    return null;
  }

  return {
    title: product.title,
    slug: product.slug,
    productGid: product.productGid,
    variantGid: product.variantGid,
    price: product.price,
    compareAtPrice: product.compareAtPrice,
    category: product.category,
    mainImage: product.mainImage,
    allowBackorder: product.allowBackorder ?? false,
    preOrderTag: product.preOrderTag,
    squareMetersPerBox: product.squareMetersPerBox ?? null,
  };
}

// Comprehensive utility to extract all product-related props for ProductButtons
export function extractProductButtonsProps(product: {
  productGid?: string | null;
  title: string;
  category: string;
  allowBackorder?: boolean | null;
  preOrderTag?: string | null;
  mainImage?: SanityImageProps | null;
  fileDownloadURL?: string | null;
  squareMetersPerBox?: number | null;
  variants?: Array<{
    gid: string | null;
    title: string;
    price: number;
    compareAtPrice?: number | null;
    option1: string;
    option2: string | null;
    option3: string | null;
  } | null>;
  sample?: {
    title: string;
    slug: string;
    productGid?: string | null;
    variantGid?: string | null;
    price: number;
    compareAtPrice?: number | null;
    category: string;
    mainImage?: SanityImageProps | null;
    allowBackorder?: boolean | null;
    preOrderTag?: string | null;
    squareMetersPerBox?: number | null;
  } | null;
}) {
  const productPageData = toProductPageData(product);
  const sampleProductData = product.sample
    ? toProductCardData(product.sample)
    : null;
  const isPreOrder = (product.allowBackorder ?? false) && !!product.preOrderTag;

  return {
    isPreOrder,
    product: productPageData,
    sampleProduct: sampleProductData,
    fileDownloadURL: product.fileDownloadURL,
  };
}
